/**
 * tripStyles — token-only inline style objects for the simplified Trip screen
 * (T27.2.7.9.3). The Trip is a READ-ONLY view of the shopping list grouped by
 * the store each item is picked at, plus one "grab anywhere" group for items
 * with no store chosen. No hex, no raw pixel literals (CP5).
 *
 * Deliberately thin: a store header + a read-only item row (thumb · name ·
 * brand/qty). The full routed itinerary — per-store totals, savings, per-item
 * price, the route line / distance-time, on-your-way suggestions, a Home stop —
 * is future work and is NOT styled here.
 *
 * The same surface renders at every width; desktop is the centered max-width
 * column from the shell wrapper, not different rows.
 */

import type { CSSProperties } from 'react';

export const pageWrapperStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
  // Mirror Shopping's own bottom reserve so the last group stays fully visible
  // above the fixed bottom nav at 390px.
  paddingBottom: 'var(--space-6)',
};

export const titleStyle: CSSProperties = {
  margin: 0,
  // T47 Phase 4 — "Your trip" page heading → Poppins display.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-semibold)',
};

export const subStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  margin: 'var(--space-1) 0 var(--space-3)',
};

// Per-store group + its header (mirrors Shopping's `.storeGroup` / `.storeHead`).
export const storeGroupStyle: CSSProperties = {
  marginBottom: 'var(--space-3q)',
};

export const storeHeadStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  margin: 'var(--space-1) var(--space-1) var(--space-2)',
};

export const storeHeadNameStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-bold)',
  fontSize: 'var(--font-size-sm)',
};

export const storeHeadCountStyle: CSSProperties = {
  fontSize: 'var(--font-size-3xs)',
  color: 'var(--color-text-muted)',
  marginLeft: 'auto',
};

// One read-only item row inside a store group. Not interactive (the Trip is a
// view, not an editor) — a plain row, no completion box, no tap target floor.
export const itemRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  width: '100%',
  boxSizing: 'border-box',
  padding: 'var(--space-3q)',
  backgroundColor: 'var(--color-bg-raised)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-lg)',
  marginBottom: 'var(--space-2)',
};

// Per-item emoji thumbnail — same compact size as the Shopping row's thumb.
export const thumbStyle: CSSProperties = {
  width: '2rem',
  height: '2rem',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-muted)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-md)',
  flex: '0 0 auto',
};

export const itemMetaStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

export const itemNameStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-base15)',
  fontWeight: 'var(--font-weight-semibold)',
  lineHeight: 'var(--line-height-tight)',
};

// The secondary line: `brand · ×qty`. One line, truncates when narrow.
export const itemSubStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 'var(--space-1)',
  fontSize: 'var(--font-size-2xs)',
  color: 'var(--color-text-secondary)',
  marginTop: 'var(--space-1)',
  lineHeight: 'var(--line-height-tight)',
};

// Honest empty state — no items, or items with no store picked yet.
export const emptyCardStyle: CSSProperties = {
  textAlign: 'center',
  padding: 'var(--space-6) var(--space-4)',
  color: 'var(--color-text-muted)',
};

export const emptyEmojiStyle: CSSProperties = {
  fontSize: 'var(--font-size-xl)',
};

export const errorStyle: CSSProperties = {
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-sm)',
  marginTop: 'var(--space-2)',
};
